// Deal Submission spec §7 — staff Deal Staging review queue.
// Mirrors the C# DTOs in PhoenixCapital.Core/DTOs/DealSubmission/DealStagingDtos.cs
// and the routes on PhoenixCapital.Api/Controllers/DealSubmission/DealStagingController.cs.

import { api } from "./client";

export type DealSubmissionStatus =
  | "Draft"
  | "Submitted"
  | "InStaging"
  | "ReturnedForChanges"
  | "Approved"
  | "Imported"
  | "Declined"
  | "Withdrawn";

/**
 * Deal Submission spec §2.4 — which door a submission came in through. Mirrors the C#
 * `DealSubmissionOrigin`. Both origins share ONE staging queue, so every
 * surface that names a counterparty has to branch on this rather than assume
 * a broker (see `stagingOriginCopy.ts`).
 */
export type DealSubmissionOrigin = "BrokerPortal" | "BuyerSpecialistEscalation";

export type DealSubmissionSectionKind = "DataEntry" | "Documents";

export type DealSubmissionSectionStatus = "NotStarted" | "InProgress" | "Complete";

export type DealSubmissionSectionReviewStatus = "Pending" | "Approved" | "Returned";

export type ScanStatus = "Pending" | "Clean" | "Infected" | "Error";

export type DealSubmissionAssessmentRunStatus =
  | "Pending"
  | "Completed"
  | "Failed"
  | "Skipped";

export type DealSubmissionEventType =
  | "DraftCreated"
  | "SectionSaved"
  | "DocumentUploaded"
  | "DocumentReplaced"
  | "DocumentScanResult"
  | "Submitted"
  | "InStaging"
  | "Returned"
  | "Resubmitted"
  | "Approved"
  | "Imported"
  | "Declined"
  | "Withdrawn"
  | "CommentAdded"
  | "SectionEditedByReviewer";

export interface DealStagingListItem {
  submissionId: string;
  brokerId: string;
  brokerName: string | null;
  title: string | null;
  status: DealSubmissionStatus;
  templateVersion: number;
  templateName: string;
  assignedReviewerUserId: string | null;
  submittedAt: string | null;
  createdAt: string;
  documentCount: number;
  flaggedDocumentCount: number;
  pipelineDealId: string | null;
  pipelineDealNumber: string | null;
  assignedReviewerDisplayName: string | null;
  brokerPortalUserId: string | null;
  // Count of counterparty responses whose attached document is not yet filed —
  // drives the "responded / action needed" queue badge.
  unfiledResponseCount: number;
  origin: DealSubmissionOrigin;
}

export interface DealSubmissionRef {
  id: string;
  brokerId: string;
  templateId: string;
  templateVersion: number;
  status: DealSubmissionStatus;
  title: string | null;
  currentSectionOrder: number;
  submittedAt: string | null;
  assignedReviewerUserId: string | null;
  importedDealId: string | null;
  createdAt: string;
  modifiedAt: string | null;
  origin: DealSubmissionOrigin;
}

export interface DealStagingSection {
  sectionStateId: string;
  sectionId: string;
  order: number;
  sectionTitle: string;
  kind: DealSubmissionSectionKind;
  formSchemaJson: string | null;
  originalDataJson: string | null;
  savedDataJson: string | null;
  status: DealSubmissionSectionStatus;
  reviewStatus?: DealSubmissionSectionReviewStatus;
  internalReviewerNote?: string | null;
  reviewedByUserId?: string | null;
  reviewedAt?: string | null;
  editedByReviewer: boolean;
  aiAssessmentMarkdown: string | null;
  aiFeedbackJson: string | null;
  assessmentStatus: DealSubmissionAssessmentRunStatus;
  hasSectionAnswersDocx?: boolean;
  hasSectionAnswersPdf?: boolean;
  hasAssessmentDocx?: boolean;
  hasAssessmentPdf?: boolean;
  reviewFiledFolderId?: string | null;
  reviewFiledAt?: string | null;
  reviewAnswersFiledDocumentId?: string | null;
  reviewAssessmentFiledDocumentId?: string | null;
  // Staging recovery — elapsed-time + timeout clock and the concise failure
  // reason for the per-section assessment.
  assessmentStartedAt?: string | null;
  assessmentError?: string | null;
}

export interface DealStagingDocument {
  documentId: string;
  documentRequestId: string;
  requestLabel: string;
  originalFileName: string;
  contentType: string;
  sizeBytes: number;
  scanStatus: ScanStatus;
  filedDocumentId: string | null;
  createdAt: string;
  // AI evaluation — per-document staff assessment / advisory feedback.
  aiAssessmentMarkdown?: string | null;
  aiFeedbackJson?: string | null;
  hasAssessmentDocx?: boolean;
  hasAssessmentPdf?: boolean;
  sectionId?: string | null;
  sectionOrder?: number;
  requestOrder?: number;
  reviewStatus?: DealSubmissionSectionReviewStatus;
  internalReviewerNote?: string | null;
  reviewedByUserId?: string | null;
  reviewedAt?: string | null;
  reviewFiledFolderId?: string | null;
  reviewFiledAt?: string | null;
  reviewAssessmentFiledDocumentId?: string | null;
  // Staging recovery — when the current scan was (re-)started (timeout clock)
  // and the concise reason it errored/timed out.
  scanStartedAt?: string | null;
  scanError?: string | null;
}


export type DealStagingReviewItemKind = "Form" | "Document";

export interface DealStagingReviewItem {
  itemId: string;
  kind: DealStagingReviewItemKind;
  sectionId: string;
  sectionOrder: number;
  sectionTitle: string;
  itemOrder: number;
  title: string;
  reviewStatus: DealSubmissionSectionReviewStatus;
  isFiled: boolean;
  internalReviewerNote: string | null;
  reviewedByUserId: string | null;
  reviewedAt: string | null;
  reviewFiledFolderId: string | null;
  reviewFiledAt: string | null;
  sectionStateId?: string | null;
  documentId?: string | null;
  documentRequestId?: string | null;
  originalFileName?: string | null;
  scanStatus?: ScanStatus | null;
  assessmentStatus: DealSubmissionAssessmentRunStatus;
  hasPrimaryPdf: boolean;
  hasAssessmentPdf: boolean;
  // A form item resolved (filed-at set) without an actual filed answer
  // document was bypassed/skipped — shown as "Skipped", not "Filed".
  isBypassed: boolean;
}

export interface DealStagingReturnComment {
  id: string;
  authorUserId: string | null;
  authorDisplayName: string | null;
  body: string;
  brokerVisible: boolean;
  sectionId: string | null;
  documentRequestId: string | null;
  resolved: boolean;
  resolvedAt: string | null;
  resolvedByPortalUserId: string | null;
  createdAt: string;
}

export interface DealStagingEvent {
  id: string;
  type: DealSubmissionEventType;
  actorUserId: string | null;
  actorPortalUserId: string | null;
  detailJson: string | null;
  createdAt: string;
}

// Append-only broker reply to a returned deal, threaded under a liaison
// comment. Viewer-only (no AI assessment); the attachment can be filed as a
// deal-level document once scan-Clean.
export interface DealSubmissionResponse {
  id: string;
  submissionId: string;
  returnCommentId: string;
  body: string;
  hasDocument: boolean;
  originalFileName: string | null;
  contentType: string | null;
  sizeBytes: number;
  scanStatus: ScanStatus;
  filedDocumentId: string | null;
  filedFolderId: string | null;
  filedAt: string | null;
  createdByPortalUserId: string | null;
  createdByDisplayName: string | null;
  createdAt: string;
}

export interface DealStagingDetail {
  submission: DealSubmissionRef;
  templateName: string;
  aiAssessmentMarkdown: string | null;
  aiFeedbackJson: string | null;
  sections: DealStagingSection[];
  documents: DealStagingDocument[];
  reviewItems: DealStagingReviewItem[];
  returnComments: DealStagingReturnComment[];
  events: DealStagingEvent[];
  assignedReviewerDisplayName: string | null;
  pipelineDealNumber: string | null;
  brokerName: string | null;
  brokerPortalUserId: string | null;
  responses?: DealSubmissionResponse[] | null;
  // Escalation — the reverse of BuyerSubmission.EscalatedDealSubmissionId.
  // Null for a broker submission, and also null when the buyer row behind an
  // escalation has been deleted, so render the link only when the id is present.
  originatingBuyerSubmissionId?: string | null;
  originatingBuyerName?: string | null;
  originatingListingName?: string | null;
}

export interface DealStagingEditSectionRequest {
  sectionId: string;
  savedDataJson: string | null;
}

export interface DealStagingReturnRequest {
  body: string;
  sectionId?: string | null;
  documentRequestId?: string | null;
}

export interface DealStagingClaimRequest {
  assignedReviewerUserId?: string | null;
}

export interface DealStagingListParams {
  status?: DealSubmissionStatus;
  brokerId?: string;
  q?: string;
  assignedReviewerUserId?: string;
  mine?: boolean;
  // Omitted means "both origins" — the queue's default, and the point of
  // keeping broker submissions and escalations in one queue.
  origin?: DealSubmissionOrigin;
}

export interface DealStagingReassignRequest {
  assignedReviewerUserId?: string | null;
}

export interface DealStagingOverrideScanRequest {
  documentId: string;
  reason: string;
}

export interface DealStagingDownload {
  url: string;
  expiresAt: string;
  // `url` is always the original file (download/open). For an Office source,
  // `previewUrl` points at a converted PDF and `isPdf` is true so the viewer
  // renders it inline; both are absent/false when no conversion applies.
  isPdf?: boolean;
  previewUrl?: string | null;
}

export interface DealStagingApproveAndFileItemRequest {
  itemId: string;
  folderId: string;
}

export interface DealStagingReturnItemRequest {
  itemId: string;
  body: string;
}

export interface DealStagingApproveReviewItemRequest {
  itemId: string;
}

export interface DealStagingFileReviewItemRequest {
  itemId: string;
  folderId: string;
}

// Deal Submission spec §11 — staff Decline.
export interface DealStagingDeclineRequest {
  brokerVisibleReason: string;
  internalReason?: string | null;
}

// Deal Submission spec §5.2 — Approve & Create Deal.
export interface DealImportRequest {
  forceCreateBusiness?: boolean;
  forceCreateBusinessReason?: string | null;
  forceCreateSeller?: boolean;
  forceCreateSellerReason?: string | null;
  forceCreateBuyer?: boolean;
  forceCreateBuyerReason?: string | null;
}

/**
 * Mirrors PhoenixCapital.Core.DTOs.DealSubmission.DealImportBlockReason —
 * a single Create Deal block reason. When `sectionId`/`fieldName` are set the
 * UI offers a "Fix this field" jump that opens that section's editor. Returned
 * in the ApiResponse `details` array on a 400 from the import endpoint.
 */
export interface DealImportBlockReason {
  message: string;
  sectionId?: string | null;
  fieldName?: string | null;
  label?: string | null;
}

export interface DealImportResult {
  dealId: string;
  dealNumber: string;
  businessId: string;
  sellerPersonId: string | null;
  sellerEntityId: string | null;
  buyerPersonId: string | null;
  buyerEntityId: string | null;
  filedDocumentIds: string[];
  skippedDocumentWarnings: string[];
  wasReRun: boolean;
  importWarnings: string[];
}

const BASE = "deal-submission/staging";

export const dealStagingApi = {
  list: (params?: DealStagingListParams) =>
    api.get<DealStagingListItem[]>(BASE, params),
  get: (id: string) => api.get<DealStagingDetail>(`${BASE}/${id}`),
  claim: (id: string, body: DealStagingClaimRequest = {}) =>
    api.post<DealStagingDetail>(`${BASE}/${id}/claim`, body),
  reassign: (id: string, body: DealStagingReassignRequest) =>
    api.post<DealStagingDetail>(`${BASE}/${id}/reassign`, body),
  editSection: (id: string, body: DealStagingEditSectionRequest) =>
    api.post<DealStagingDetail>(`${BASE}/${id}/section-edit`, body),
  return: (id: string, body: DealStagingReturnRequest) =>
    api.post<DealStagingDetail>(`${BASE}/${id}/return`, body),
  approveRemainingSections: (id: string) =>
    api.post<DealStagingDetail>(`${BASE}/${id}/sections/approve-remaining`),
  generateSectionAnswers: (id: string, sectionId: string) =>
    api.post<DealStagingDetail>(`${BASE}/${id}/sections/${sectionId}/generate-answers`),
  generateDocumentAssessment: (id: string, documentId: string) =>
    api.post<DealStagingDetail>(`${BASE}/${id}/documents/${documentId}/generate-assessment`),
  sectionAnswersDownloadUrl: (id: string, sectionId: string, kind: "pdf" | "docx") =>
    api.get<DealStagingDownload>(`${BASE}/${id}/sections/${sectionId}/answers-download-url`, { kind }),
  sectionAssessmentDownloadUrl: (id: string, sectionId: string, kind: "pdf" | "docx") =>
    api.get<DealStagingDownload>(`${BASE}/${id}/sections/${sectionId}/assessment-download-url`, { kind }),
  documentDownloadUrl: (id: string, documentId: string) =>
    api.get<DealStagingDownload>(`${BASE}/${id}/documents/${documentId}/download-url`),
  documentAssessmentDownloadUrl: (id: string, documentId: string, kind: "pdf" | "docx") =>
    api.get<DealStagingDownload>(`${BASE}/${id}/documents/${documentId}/assessment-download-url`, { kind }),
  approveAndFileItem: (id: string, body: DealStagingApproveAndFileItemRequest) =>
    api.post<DealStagingDetail>(`${BASE}/${id}/review-items/approve-and-file`, body),
  approveReviewItem: (id: string, body: DealStagingApproveReviewItemRequest) =>
    api.post<DealStagingDetail>(`${BASE}/${id}/review-items/approve`, body),
  fileReviewItem: (id: string, body: DealStagingFileReviewItemRequest) =>
    api.post<DealStagingDetail>(`${BASE}/${id}/review-items/file`, body),
  // Skip-file a form item whose answer document cannot be generated so it
  // stops blocking Create Deal. Marks it resolved without a filed document.
  bypassReviewItem: (id: string, body: { itemId: string }) =>
    api.post<DealStagingDetail>(`${BASE}/${id}/review-items/bypass`, body),
  returnReviewItem: (id: string, body: DealStagingReturnItemRequest) =>
    api.post<DealStagingDetail>(`${BASE}/${id}/review-items/return`, body),
  // Append-only broker responses — viewer-only document + file deal-level.
  listResponses: (id: string) =>
    api.get<DealSubmissionResponse[]>(`${BASE}/${id}/responses`),
  responseDownloadUrl: (id: string, responseId: string) =>
    api.get<DealStagingDownload>(`${BASE}/${id}/responses/${responseId}/download-url`),
  fileResponse: (id: string, responseId: string, body: { folderId: string }) =>
    api.post<DealStagingDetail>(`${BASE}/${id}/responses/${responseId}/file`, body),
  decline: (id: string, body: DealStagingDeclineRequest) =>
    api.post<DealStagingDetail>(`${BASE}/${id}/decline`, body),
  overrideScan: (id: string, body: DealStagingOverrideScanRequest) =>
    api.post<DealStagingDetail>(`${BASE}/${id}/override-scan`, body),
  // Staging recovery — manual recovery controls for stuck async jobs.
  regenerateAssessment: (id: string, sectionStateId: string) =>
    api.post<DealStagingDetail>(`${BASE}/${id}/sections/${sectionStateId}/regenerate-assessment`, {}),
  refreshScan: (id: string, documentId: string) =>
    api.post<DealStagingDetail>(`${BASE}/${id}/documents/${documentId}/refresh-scan`, {}),
  import: (id: string, body: DealImportRequest = {}) =>
    api.post<DealImportResult>(`deal-submission/${id}/import`, body),
};
